import { Component, inject, TemplateRef, viewChild, signal, AfterViewInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, catchError, of, switchMap, map, BehaviorSubject, Subject, debounceTime, shareReplay, withLatestFrom } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AdmissionState, ActivityState, PartnerListItem, PageQueryParams, PaginatedList } from '../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../services/organizations/organization.service';
import { ProtectedContentComponent } from '../../../../components/organisms/protected-content/protected-content.component';
import { NotificationService } from '../../../../services/form/notification.service';
import { NavigationService } from '../../../../services/navigation.service';
import { BadgeComponent, ButtonComponent, TableComponent, ColumnConfig, ColumnType, FilterOption } from "htx-ui-library";

@Component({
  selector: 'app-partnernetwork-overview-page',
  imports: [CommonModule, AsyncPipe, ProtectedContentComponent, ButtonComponent, TableComponent, BadgeComponent, TranslocoPipe],
  templateUrl: './partnernetwork-overview-page.component.html',
  styleUrl: './partnernetwork-overview-page.component.scss'
})
export class PartnerNetworkOverviewPageComponent implements AfterViewInit {
  private router = inject(Router);
  private organizationService = inject(OrganizationService);
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);

  requiredRoles = this.navigationService.getAccessRoles('partnernetwork');

  statusTemplateRef = viewChild<TemplateRef<PartnerListItem>>('columnStatus');
  actionsTemplateRef = viewChild<TemplateRef<PartnerListItem>>('columnActions');

  private statusTemplate = signal<TemplateRef<PartnerListItem> | undefined>(undefined);
  private actionsTemplate = signal<TemplateRef<PartnerListItem> | undefined>(undefined);
  private templatesReady = signal(false);

  // Pagination and sorting state
  private readonly queryParamsSubject$ = new BehaviorSubject<PageQueryParams>({
    page: 0,
    size: 5
  });
  readonly queryParams$ = this.queryParamsSubject$.asObservable();

  // Filter change subject with debounce
  private readonly filterChange$ = new Subject<{ columnName: string; value: string }>();

  // Paginated response (includes both data and pagination metadata)
  private paginatedResponse$ = this.queryParamsSubject$.pipe(
    switchMap(params => this.organizationService.findPartnersWithPagination(params)),
    catchError((error) => {
      this.notificationService.showError('partnernetwork.overview.loadingError', 5000);
      console.error('Error loading partners:', error);
      return of({ content: [], pagination: { pageNumber: 0, pageSize: 10, totalElements: 0, totalPages: 0, last: true, first: true, numberOfElements: 0, size: 10, number: 0, empty: true } } as PaginatedList<PartnerListItem>);
    }),
    shareReplay(1)
  );

  data$: Observable<PartnerListItem[]> = this.paginatedResponse$.pipe(
    map(result => result.content)
  );

  totalElements$: Observable<number> = this.paginatedResponse$.pipe(
    map(result => result.pagination.totalElements)
  );

  columnConfig$: Observable<ColumnConfig<PartnerListItem>[]> = this.translocoService.selectTranslate([]).pipe(
    switchMap(() => {
      // Wait for templates to be ready before building config
      return new Observable<ColumnConfig<PartnerListItem>[]>(observer => {
        const checkTemplates = () => {
          if (this.templatesReady()) {
            observer.next(this.buildColumnConfig());
            observer.complete();
          } else {
            setTimeout(checkTemplates, 50);
          }
        };
        checkTemplates();
      });
    })
  );

  private buildColumnConfig(): ColumnConfig<PartnerListItem>[] {
    const statusTmpl = this.statusTemplate();
    const actionsTmpl = this.actionsTemplate();

    return [
      {
        name: 'bpnl',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.bpnl.label'),
        cellContent: (rowData: PartnerListItem) => rowData.bpnl,
      },
      {
        name: 'legalName',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.partner.label'),
        cellContent: (rowData: PartnerListItem) => rowData.legalName,
        sortable: true,
        filterable: true
      },
      {
        name: 'country',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.country.label'),
        cellContent: (rowData: PartnerListItem) => rowData.country,
        sortable: true,
      },
      {
        name: 'activityState',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.activityState.label'),
        template: statusTmpl as any,
        filterable: true,
        filterOptions: [
          { value: ActivityState.ACTIVE, label: this.translocoService.translate('organization.control.activityState.options.ACTIVE') },
          { value: ActivityState.INACTIVE, label: this.translocoService.translate('organization.control.activityState.options.INACTIVE') }
        ]
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.action.label'),
        template: actionsTmpl as any
      }
    ];
  }

  ngAfterViewInit(): void {
    this.statusTemplate.set(this.statusTemplateRef());
    this.actionsTemplate.set(this.actionsTemplateRef());
    // Signal that templates are ready
    this.templatesReady.set(true);

    // Subscribe to debounced filter changes (1000ms delay)
    this.filterChange$
      .pipe(
        debounceTime(1000),
        withLatestFrom(this.queryParams$),
        // Apply the filter change to query params
        switchMap(([{ columnName, value }, currentParams]) => {
          const newParams: PageQueryParams = {
            ...currentParams,
            page: 0 // Reset to first page when filter changes
          };

          // Map column name to API filter parameter
          if (value) {
            newParams[columnName] = value;
          }

          this.queryParamsSubject$.next(newParams);
          return of(null);
        })
      )
      .subscribe();

    // Mark as initialized and trigger initial load
    this.queryParamsSubject$.next(this.queryParamsSubject$.value);
  }

  /**
   * Handle pagination changes from table
   * @param event Pagination event with pageIndex and pageSize
   */
  onPageChange(event: any): void {
    const currentParams = this.queryParamsSubject$.value;
    const newPage = event.pageIndex ?? 0;
    const newSize = event.pageSize ?? 5;

    // If both page and size are unchanged, skip update
    if (currentParams.page === newPage && currentParams.size === newSize) {
      return;
    }

    // If pageSize changed, reset to first page
    const finalPage = currentParams.size !== newSize ? 0 : newPage;

    this.queryParamsSubject$.next({
      ...currentParams,
      page: finalPage,
      size: newSize
    });
  }

  /**
   * Handle sorting changes from table
   * @param event Sort event with columnName and direction
   */
  onSortChange(event: any): void {
    const currentParams = this.queryParamsSubject$.value;
    let sort: string | undefined;

    if (event && event.columnName && event.direction) {
      sort = `${event.columnName},${event.direction}`;
    }

    this.queryParamsSubject$.next({
      ...currentParams,
      sort,
      page: 0 // Reset to first page when sorting changes
    });
  }

  /**
   * Handle filtering changes from table
   * @param event Filter event with columnName and value
   */
  onFilterChange(event: any): void {
    // Push to the debounced filter subject instead of directly updating queryParams
    if (event && event.columnName) {
      this.filterChange$.next({ columnName: event.columnName, value: event.value || '' });
    }
  }

  onRowClick(data: PartnerListItem): void {
    this.goToDetails(data);
  }

  goToDetails(data: PartnerListItem): void {
    this.router.navigate(['partnernetwork', 'details', data.id]);
  }

  getTyped(row: unknown) {
    return row as PartnerListItem;
  }

  protected readonly AdmissionState = AdmissionState;
  protected readonly ActivityState = ActivityState;
}
